import type { AppLocale } from '@/lib/i18n'
import { currentLocale, setLocale } from '@/lib/i18n'
import { ApiError } from '@/lib/api/errors'
import {
  useSessionStore,
  type SessionUser,
} from '@/lib/auth/session-store'
import { useUiStore } from '@/lib/ui/ui-store'
import type { TokenPair } from '@/lib/api/types'
import { isUnauthorized } from '@/lib/api/friendly-errors'
import { disableNativePush } from '@/features/notifications/push/native-push'

import { authApi, type PrivateProfile } from './api'
import { useGateStore } from './state/gate-store'

export interface LoginOutcome {
  profile: PrivateProfile | null
  requiresTermsAcceptance: boolean
}

export function toSessionUser(profile: PrivateProfile): SessionUser {
  return {
    id: profile.id,
    email: profile.email,
    isAdmin: profile.isAdmin,
    locale: profile.locale,
  }
}

/**
 * Applies the authenticated profile to the in-memory session and lets the
 * profile locale win across devices (D-038).
 */
export async function applyAuthenticatedProfile(
  profile: PrivateProfile,
): Promise<void> {
  const accessToken = useSessionStore.getState().accessToken
  useSessionStore.getState().setSession({
    accessToken,
    user: toSessionUser(profile),
  })
  await syncLocaleFromProfile(profile.locale)
}

export async function syncLocaleFromProfile(locale: AppLocale): Promise<void> {
  if (locale !== currentLocale()) {
    await setLocale(locale)
  }
}

/**
 * Finishes login/register: keeps the access token in memory, loads `/me`, and
 * asks the server whether the terms gate applies. A legal-status failure does
 * not destroy a valid session; the server-side gate remains authoritative and
 * the next startup retries.
 */
export async function completeLogin(tokens: TokenPair): Promise<LoginOutcome> {
  await useSessionStore.getState().applyTokens(tokens)

  let profile: PrivateProfile
  try {
    profile = await authApi.getMe()
  } catch (error) {
    if (
      error instanceof ApiError &&
      error.code === 'TERMS_ACCEPTANCE_REQUIRED'
    ) {
      // The login succeeded but the server gates `/me` behind terms acceptance.
      // Keep the session, flag the gate and let the user accept.
      useGateStore.getState().setTermsRequired(true)
      try {
        const legal = await authApi.legalStatus()
        useGateStore.getState().setLegalStatus({
          requiresTermsAcceptance: legal.requiresTermsAcceptance,
          currentTermsVersion: legal.currentTermsVersion,
        })
      } catch {
        // The gate is already flagged; TermsGate retries legal status itself.
      }
      return { profile: null, requiresTermsAcceptance: true }
    }
    if (isUnauthorized(error)) useSessionStore.getState().clearSession()
    throw error
  }

  await applyAuthenticatedProfile(profile)

  let requiresTermsAcceptance = false
  try {
    const legal = await authApi.legalStatus()
    requiresTermsAcceptance = legal.requiresTermsAcceptance
    useGateStore.getState().setLegalStatus({
      requiresTermsAcceptance: legal.requiresTermsAcceptance,
      currentTermsVersion: legal.currentTermsVersion,
    })
  } catch {
    // Fail open on this client convenience gate; every protected backend call
    // still enforces the server-side terms acceptance (P0-2).
  }

  return { profile, requiresTermsAcceptance }
}

/** Best-effort server revocation, then a full local cleanup. */
export async function logoutAccount(): Promise<void> {
  // Remove the native device row while the access token is still valid.
  await disableNativePush()
  try {
    await authApi.logout()
  } catch {
    // The local session must end even when the network is unavailable.
  }
  useSessionStore.getState().clearSession()
  useGateStore.getState().resetGates()
  useUiStore.getState().setPendingActivityPath(null)
}

function safeInternalPath(value: unknown): string | null {
  return typeof value === 'string' &&
    value.startsWith('/') &&
    !value.startsWith('//')
    ? value
    : null
}

/**
 * Approved login destination order: profile setup, pending activity deep link,
 * the route the guard preserved, then discovery.
 */
export function postAuthDestination({
  setupProfile = false,
  from,
  pendingActivityPath,
}: {
  setupProfile?: boolean
  from?: unknown
  pendingActivityPath?: string | null
}): string {
  if (setupProfile) return '/profile'
  return (
    safeInternalPath(pendingActivityPath) ??
    safeInternalPath(from) ??
    '/home'
  )
}
